import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  LogOut, LayoutDashboard, ListChecks, Users, Wallet,
  TrendingUp, AlertTriangle, CheckCircle2, IndianRupee,
  CreditCard, FileImage, Search, Loader2, RefreshCw,
  PlusCircle, XCircle, Clock,
} from 'lucide-react';
import PartnerAnalyticsPanel from './PartnerAnalyticsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RS = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function PartnerPortalShell() {
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [tab, setTab] = useState('dashboard');

  // Auth bootstrap
  useEffect(() => {
    const t = localStorage.getItem('partner_portal_token');
    if (!t) { navigate('/partner-portal/login'); return; }
    axios.get(`${API}/partner-portal/me`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => setPartner(r.data))
      .catch(() => {
        localStorage.removeItem('partner_portal_token');
        navigate('/partner-portal/login');
      });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem('partner_portal_token');
    localStorage.removeItem('partner_portal_partner');
    navigate('/partner-portal/login');
  };

  // Bind a pre-authed axios instance for child components
  const auth = useCallback((opts = {}) => ({
    ...opts,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('partner_portal_token')}`,
      ...(opts.headers || {}),
    },
  }), []);

  if (!partner) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-indigo-600 text-white flex items-center justify-center font-bold">
              {(partner.name || 'P').charAt(0)}
            </div>
            <div>
              <p className="font-semibold text-slate-900">{partner.name}</p>
              <p className="text-xs text-slate-500">Distributor Portal · Watcho API</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right" data-testid="pp-balance">
              <p className="text-[10px] text-slate-500 uppercase">Wallet Balance</p>
              <p className="text-base font-bold text-emerald-600">{RS(partner.balance)}</p>
            </div>
            <Button variant="outline" size="sm" onClick={logout} data-testid="pp-logout">
              <LogOut className="h-4 w-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-5 max-w-3xl">
            <TabsTrigger value="dashboard" data-testid="pp-tab-dashboard"><LayoutDashboard className="h-4 w-4 mr-1" />Dashboard</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="pp-tab-analytics"><TrendingUp className="h-4 w-4 mr-1" />Analytics</TabsTrigger>
            <TabsTrigger value="activations" data-testid="pp-tab-activations"><ListChecks className="h-4 w-4 mr-1" />Activations</TabsTrigger>
            <TabsTrigger value="customers" data-testid="pp-tab-customers"><Users className="h-4 w-4 mr-1" />Customers</TabsTrigger>
            <TabsTrigger value="wallet" data-testid="pp-tab-wallet"><Wallet className="h-4 w-4 mr-1" />Wallet</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-4">
            <DashboardTab auth={auth} partner={partner} refreshPartner={(p) => setPartner(p)} />
          </TabsContent>
          <TabsContent value="analytics" className="mt-4">
            <PartnerAnalyticsPanel
              endpoint={`${API}/partner-portal/analytics`}
              authHeaders={{ Authorization: `Bearer ${localStorage.getItem('partner_portal_token')}` }}
              showPartnerSelector={false}
            />
          </TabsContent>
          <TabsContent value="activations" className="mt-4">
            <ActivationsTab auth={auth} />
          </TabsContent>
          <TabsContent value="customers" className="mt-4">
            <CustomersTab auth={auth} />
          </TabsContent>
          <TabsContent value="wallet" className="mt-4">
            <WalletTab auth={auth} partner={partner} refreshPartner={(p) => setPartner(p)} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// ====== Dashboard ======
function DashboardTab({ auth, partner, refreshPartner }) {
  const [stats, setStats] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/partner-portal/stats`, auth()).then((r) => setStats(r.data));
    axios.get(`${API}/partner-portal/me`, auth()).then((r) => refreshPartner(r.data));
  }, [auth, refreshPartner]);
  useEffect(() => { load(); }, [load]);

  if (!stats) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  const successPct = stats.total_activations
    ? Math.round((stats.success_count / stats.total_activations) * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Wallet} label="Wallet Balance" value={RS(stats.balance)} color="emerald" testId="pp-stat-balance" />
        <StatCard icon={TrendingUp} label="Total Activation Calls"
                  value={stats.total_activations} color="indigo"
                  hint="every subscribe API call counted"
                  testId="pp-stat-total" />
        <StatCard icon={CheckCircle2} label="Successful Activations"
                  value={stats.success_count} color="emerald"
                  hint={`${successPct}% success · includes renewals`}
                  testId="pp-stat-success" />
        <StatCard icon={AlertTriangle} label="Failed Activations"
                  value={stats.failed_count} color="red"
                  hint="errors / rejections"
                  testId="pp-stat-failed" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4" /> Active Customers (unique)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-6 flex-wrap">
            <div>
              <p className="text-3xl font-bold text-slate-900" data-testid="pp-active-customers">
                {stats.active_customers}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Distinct mobile numbers with at least one successful Watcho activation
              </p>
            </div>
            {stats.active_customers > 0 && (
              <div className="text-xs text-slate-500 bg-slate-50 border rounded-md px-3 py-2">
                <p>
                  <span className="font-semibold text-slate-700">
                    {(stats.success_count / Math.max(stats.active_customers, 1)).toFixed(2)}
                  </span>{' '}
                  successful activations per customer on average
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  (renewals, plan upgrades & repeat purchases inflate the "Successful" count)
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="py-4 text-sm">
          <p className="font-semibold text-amber-900 mb-1">⚡ Low Balance Warning</p>
          {stats.balance < 200 ? (
            <p className="text-amber-800">
              Your wallet balance is below ₹200 — top up now to avoid failed activations.
            </p>
          ) : (
            <p className="text-amber-800/70">Balance is healthy. We'll alert here when it dips below ₹200.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color = 'indigo', hint, testId }) {
  const tones = {
    emerald: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    indigo: 'text-indigo-600 bg-indigo-50 border-indigo-200',
    red: 'text-red-600 bg-red-50 border-red-200',
    amber: 'text-amber-600 bg-amber-50 border-amber-200',
  };
  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
          <div className={`w-7 h-7 rounded-full flex items-center justify-center border ${tones[color]}`}>
            <Icon className="h-3.5 w-3.5" />
          </div>
        </div>
        <p className="text-2xl font-bold text-slate-900 mt-2" data-testid={testId}>{value}</p>
        {hint && <p className="text-[10px] text-slate-400 mt-1">{hint}</p>}
      </CardContent>
    </Card>
  );
}

// ====== Activations ======
function ActivationsTab({ auth }) {
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [inspect, setInspect] = useState(null); // selected row for inspector
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status, limit: '100' });
      if (q) params.append('q', q);
      const r = await axios.get(`${API}/partner-portal/activations?${params.toString()}`, auth());
      setData(r.data);
    } finally { setLoading(false); }
  }, [auth, status, q]);
  useEffect(() => { load(); }, [load]);

  const openInspector = async (row) => {
    setInspect(row);
    setDetail(null);
    setDetailLoading(true);
    try {
      const r = await axios.get(`${API}/partner-portal/activations/${row.activation_id}`, auth());
      setDetail(r.data.activation);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load activation');
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
        <CardTitle className="text-base">Activation Logs</CardTitle>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[150px]" data-testid="pp-activations-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="failure">Failure</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative w-[240px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="mobile / ref / order id"
                   value={q} onChange={(e) => setQ(e.target.value)}
                   data-testid="pp-activations-search" />
          </div>
          <Button variant="outline" size="icon" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="text-left p-2">Date</th>
                <th className="text-left p-2">Mobile</th>
                <th className="text-left p-2">Plan</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Watcho Order</th>
                <th className="text-right p-2">Amount</th>
                <th className="text-left p-2">Failure Reason</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-8 text-slate-400">No activations</td></tr>
              ) : data.rows.map((r) => (
                <tr key={r.activation_id} className="border-t hover:bg-slate-50">
                  <td className="p-2 text-xs">{r.created_at?.slice(0, 19).replace('T', ' ')}</td>
                  <td className="p-2 font-mono">{r.mobile_no}</td>
                  <td className="p-2">{r.plan_name} <span className="text-xs text-slate-400">({r.plan_id})</span></td>
                  <td className="p-2">
                    {r.provider_status === 'activated' ? (
                      <Badge className="bg-emerald-100 text-emerald-700">activated</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700">{r.provider_status || 'failed'}</Badge>
                    )}
                  </td>
                  <td className="p-2 font-mono text-xs">{r.watcho_order_id || '—'}</td>
                  <td className="p-2 text-right">{RS(r.partner_price || r.watcho_price)}</td>
                  <td className="p-2 text-xs text-red-600 max-w-[300px] truncate" title={r.provider_error}>
                    {r.provider_error || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-400 mt-2">Total: {data.total}</p>
      </CardContent>

      <ActivationInspectorDialog
        open={!!inspect}
        onClose={() => { setInspect(null); setDetail(null); }}
        loading={detailLoading}
        detail={detail}
        summary={inspect}
      />
    </Card>
  );
}

// ====== Activation Inspector (read-only for partner) ======
function ActivationInspectorDialog({ open, onClose, loading, detail, summary }) {
  const d = detail || summary || {};
  const jsonBlock = (label, val) => (
    <div className="space-y-1">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <pre className="bg-slate-900 text-emerald-200 text-xs p-3 rounded max-h-64 overflow-auto whitespace-pre-wrap break-all">
        {val ? JSON.stringify(val, null, 2) : '— No data —'}
      </pre>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Activation Inspector
            {d.provider_status === 'activated' ? (
              <Badge className="bg-emerald-100 text-emerald-700">activated</Badge>
            ) : (
              <Badge className="bg-red-100 text-red-700">{d.provider_status || 'failed'}</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Full Watcho request, response, and error details for this activation.
          </DialogDescription>
        </DialogHeader>
        {loading && !detail ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-3 text-sm max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Activation ID" value={d.activation_id} mono />
              <Field label="Created" value={(d.created_at || '').slice(0, 19).replace('T', ' ')} />
              <Field label="Mobile" value={d.mobile_no} mono />
              <Field label="Plan" value={`${d.plan_name || ''} (${d.plan_id || ''})`} />
              <Field label="External Ref" value={d.external_ref} mono />
              <Field label="ISP Txn No" value={d.isp_txn_no} mono />
              <Field label="Watcho Order ID" value={d.watcho_order_id || '—'} mono />
              <Field label="HTTP Status (upstream)" value={d.provider_http_status || '—'} />
              <Field label="Retries" value={d.retry_count ?? 0} />
              <Field label="Amount" value={RS(d.partner_price || d.watcho_price)} />
            </div>
            {d.provider_error ? (
              <div className="bg-red-50 border border-red-200 rounded p-3">
                <p className="text-xs uppercase tracking-wide text-red-600 mb-1">Failure Reason</p>
                <p className="text-sm text-red-800 break-words">{d.provider_error}</p>
              </div>
            ) : null}
            {jsonBlock('Request sent to Watcho', d.provider_request)}
            {jsonBlock('Response from Watcho', d.provider_response || d.provider_result)}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="pp-inspect-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-sm break-all ${mono ? 'font-mono' : ''}`}>{value || '—'}</p>
    </div>
  );
}

// ====== Customers ======
function CustomersTab({ auth }) {
  const [status, setStatus] = useState('active');
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState({ rows: [], count: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status });
      if (q) params.append('q', q);
      if (dateFrom) params.append('from', dateFrom);
      if (dateTo) params.append('to', dateTo);
      const r = await axios.get(`${API}/partner-portal/customers?${params.toString()}`, auth());
      setData(r.data);
    } finally { setLoading(false); }
  }, [auth, status, q, dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  const STATUS_META = {
    active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700' },
    inactive: { label: 'Inactive', color: 'bg-slate-200 text-slate-700' },
    failed: { label: 'Failed', color: 'bg-red-100 text-red-700' },
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="text-base">Customers</CardTitle>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <Label className="text-[10px] text-slate-500">From</Label>
              <Input type="date" value={dateFrom}
                     onChange={(e) => setDateFrom(e.target.value)}
                     className="w-[140px]" data-testid="pp-customers-from" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">To</Label>
              <Input type="date" value={dateTo}
                     onChange={(e) => setDateTo(e.target.value)}
                     className="w-[140px]" data-testid="pp-customers-to" />
            </div>
            <div className="relative w-[200px]">
              <Search className="h-4 w-4 absolute left-3 top-[26px] -translate-y-1/2 text-slate-400" />
              <Label className="text-[10px] text-slate-500">Search</Label>
              <Input className="pl-9" placeholder="mobile"
                     value={q} onChange={(e) => setQ(e.target.value)}
                     data-testid="pp-customers-search" />
            </div>
            <Button variant="outline" size="icon" onClick={load} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        <Tabs value={status} onValueChange={setStatus} className="mt-2">
          <TabsList>
            <TabsTrigger value="active" data-testid="pp-cust-tab-active">Active</TabsTrigger>
            <TabsTrigger value="inactive" data-testid="pp-cust-tab-inactive">Inactive</TabsTrigger>
            <TabsTrigger value="failed" data-testid="pp-cust-tab-failed">Failed</TabsTrigger>
            <TabsTrigger value="all" data-testid="pp-cust-tab-all">All</TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="text-left p-2">Mobile</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Last Plan</th>
                <th className="text-left p-2">Duration</th>
                <th className="text-left p-2">Last Activated</th>
                <th className="text-right p-2">Attempts</th>
                <th className="text-right p-2">Successful</th>
                <th className="text-right p-2">Last Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-8 text-slate-400">No customers</td></tr>
              ) : data.rows.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.failed;
                return (
                  <tr key={r.mobile_no} className="border-t hover:bg-slate-50">
                    <td className="p-2 font-mono">{r.mobile_no}</td>
                    <td className="p-2"><Badge className={meta.color}>{meta.label}</Badge></td>
                    <td className="p-2">{r.last_plan_name || '—'}</td>
                    <td className="p-2"><Badge variant="outline" className="text-xs">{r.duration || '—'}</Badge></td>
                    <td className="p-2 text-xs">{r.last_activation_at?.slice(0, 19).replace('T', ' ')}</td>
                    <td className="p-2 text-right">{r.total_attempts}</td>
                    <td className="p-2 text-right text-emerald-700">{r.success_count}</td>
                    <td className="p-2 text-right">{RS(r.last_amount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-400 mt-2">Total: {data.count}</p>
      </CardContent>
    </Card>
  );
}

// ====== Wallet ======
function WalletTab({ auth, partner, refreshPartner }) {
  const [data, setData] = useState({ rows: [], total: 0, balance: partner.balance });
  const [manual, setManual] = useState({ rows: [], count: 0 });
  const [rechargeDialog, setRechargeDialog] = useState(false);
  const [mode, setMode] = useState('easebuzz');     // easebuzz | manual
  const [amount, setAmount] = useState('');
  const [manualForm, setManualForm] = useState({
    payment_mode: 'bank_transfer', reference_number: '', proof_image_base64: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    const tx = await axios.get(`${API}/partner-portal/wallet/transactions?limit=100`, auth());
    setData(tx.data);
    const me = await axios.get(`${API}/partner-portal/me`, auth());
    refreshPartner(me.data);
    const mr = await axios.get(`${API}/partner-portal/recharge/manual`, auth());
    setManual(mr.data);
  }, [auth, refreshPartner]);
  useEffect(() => { load(); }, [load]);

  // Handle Easebuzz callback redirect to /partner-portal/wallet?payment=success
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      toast.success(`Recharge of ₹${params.get('amount') || ''} successful!`);
      window.history.replaceState({}, '', '/partner-portal/wallet');
      load();
    } else if (params.get('payment') === 'failed') {
      toast.error('Recharge failed. Please try again or contact support.');
      window.history.replaceState({}, '', '/partner-portal/wallet');
    }
  }, [load]);

  const onProofFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error('File too large (max 4MB)'); return; }
    const reader = new FileReader();
    reader.onload = () => setManualForm({ ...manualForm, proof_image_base64: reader.result });
    reader.readAsDataURL(f);
  };

  const submitRecharge = async () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error('Amount must be > 0'); return; }
    setSubmitting(true);
    try {
      if (mode === 'easebuzz') {
        const r = await axios.post(`${API}/partner-portal/recharge/easebuzz/initiate`,
                                   { amount: parseFloat(amount) }, auth());
        // Redirect user to Easebuzz hosted page
        window.location.href = r.data.payment_url;
      } else {
        if (!manualForm.reference_number) { toast.error('Reference number required'); return; }
        await axios.post(`${API}/partner-portal/recharge/manual`, {
          amount: parseFloat(amount),
          payment_mode: manualForm.payment_mode,
          reference_number: manualForm.reference_number,
          proof_image_base64: manualForm.proof_image_base64 || null,
          notes: manualForm.notes || null,
        }, auth());
        toast.success('Recharge request submitted! Admin will review & approve.');
        setRechargeDialog(false);
        setAmount(''); setManualForm({ payment_mode: 'bank_transfer', reference_number: '', proof_image_base64: '', notes: '' });
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const STATUS_BADGE = {
    pending: 'bg-amber-100 text-amber-700',
    approved: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-700',
    success: 'bg-emerald-100 text-emerald-700',
    failed: 'bg-red-100 text-red-700',
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="md:col-span-2 bg-gradient-to-br from-indigo-600 to-indigo-700 text-white border-0">
          <CardContent className="py-6">
            <p className="text-xs text-indigo-100 uppercase tracking-wide">Current Balance</p>
            <p className="text-4xl font-bold mt-1" data-testid="pp-wallet-balance">{RS(data.balance)}</p>
            <p className="text-xs text-indigo-100 mt-1">{data.total} transactions</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-6 flex flex-col justify-center">
            <Button onClick={() => setRechargeDialog(true)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="pp-recharge-btn">
              <PlusCircle className="h-4 w-4 mr-2" /> Recharge Wallet
            </Button>
            <p className="text-xs text-slate-500 mt-2 text-center">Easebuzz · Bank transfer</p>
          </CardContent>
        </Card>
      </div>

      {/* Pending manual recharges */}
      {manual.rows.filter(m => m.status === 'pending').length > 0 && (
        <Card className="border-amber-200 bg-amber-50/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-amber-900 flex items-center gap-2">
              <Clock className="h-4 w-4" /> Pending Manual Recharge Requests
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {manual.rows.filter(m => m.status === 'pending').map(m => (
              <div key={m.request_id} className="text-sm flex justify-between items-center border-b last:border-0 pb-2">
                <div>
                  <span className="font-semibold">{RS(m.amount)}</span>
                  <span className="text-xs text-slate-500 ml-2">{m.payment_mode} · Ref: {m.reference_number}</span>
                </div>
                <Badge className={STATUS_BADGE.pending}>{m.status}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Transaction history */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Transaction History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs">
                <tr>
                  <th className="text-left p-2">Date</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-right p-2">Amount</th>
                  <th className="text-right p-2">Balance After</th>
                  <th className="text-left p-2">Mode</th>
                  <th className="text-left p-2">Reference</th>
                  <th className="text-left p-2">Description</th>
                  <th className="text-left p-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 ? (
                  <tr><td colSpan="8" className="text-center py-8 text-slate-400">No transactions</td></tr>
                ) : data.rows.map((r) => (
                  <tr key={r.txn_id} className="border-t hover:bg-slate-50">
                    <td className="p-2 text-xs">{r.created_at?.slice(0, 19).replace('T', ' ')}</td>
                    <td className="p-2">
                      <Badge className={r.type === 'credit' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}>
                        {r.type}
                      </Badge>
                    </td>
                    <td className={`p-2 text-right font-semibold ${r.type === 'credit' ? 'text-emerald-700' : 'text-orange-700'}`}>
                      {r.type === 'credit' ? '+' : '-'}{RS(r.amount)}
                    </td>
                    <td className="p-2 text-right">{r.balance_after != null ? RS(r.balance_after) : '—'}</td>
                    <td className="p-2 text-xs">{r.payment_mode || '—'}</td>
                    <td className="p-2 text-xs font-mono">{r.reference_id || '—'}</td>
                    <td className="p-2 text-xs">{r.description}</td>
                    <td className="p-2">
                      {r.status && <Badge className={STATUS_BADGE[r.status] || 'bg-slate-100 text-slate-700'}>{r.status}</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Recharge dialog */}
      <Dialog open={rechargeDialog} onOpenChange={setRechargeDialog}>
        <DialogContent className="sm:max-w-md" data-testid="pp-recharge-dialog">
          <DialogHeader>
            <DialogTitle>Recharge Wallet</DialogTitle>
            <DialogDescription>Choose how you want to top up.</DialogDescription>
          </DialogHeader>
          <Tabs value={mode} onValueChange={setMode}>
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="easebuzz" data-testid="pp-recharge-easebuzz">
                <CreditCard className="h-4 w-4 mr-1" /> Easebuzz
              </TabsTrigger>
              <TabsTrigger value="manual" data-testid="pp-recharge-manual">
                <FileImage className="h-4 w-4 mr-1" /> Bank / UPI Proof
              </TabsTrigger>
            </TabsList>
            <TabsContent value="easebuzz" className="space-y-3 mt-3">
              <div>
                <Label>Amount (Rs.)</Label>
                <div className="relative">
                  <IndianRupee className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input type="number" min="1" className="pl-9" value={amount}
                         onChange={(e) => setAmount(e.target.value)}
                         data-testid="pp-recharge-amount" />
                </div>
              </div>
              <p className="text-xs text-slate-500">You'll be redirected to Easebuzz to complete payment. Wallet is credited automatically on success.</p>
            </TabsContent>
            <TabsContent value="manual" className="space-y-3 mt-3">
              <div>
                <Label>Amount (Rs.)</Label>
                <Input type="number" min="1" value={amount}
                       onChange={(e) => setAmount(e.target.value)}
                       data-testid="pp-recharge-manual-amount" />
              </div>
              <div>
                <Label>Payment Mode</Label>
                <Select value={manualForm.payment_mode}
                        onValueChange={(v) => setManualForm({ ...manualForm, payment_mode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer / NEFT</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reference Number *</Label>
                <Input value={manualForm.reference_number}
                       onChange={(e) => setManualForm({ ...manualForm, reference_number: e.target.value })}
                       placeholder="UTR / UPI Txn / Cheque No."
                       data-testid="pp-manual-ref" />
              </div>
              <div>
                <Label>Payment Proof (image)</Label>
                <Input type="file" accept="image/*" onChange={onProofFile}
                       data-testid="pp-manual-proof" />
                {manualForm.proof_image_base64 && (
                  <img src={manualForm.proof_image_base64} alt="proof"
                       className="mt-2 max-h-32 rounded border" />
                )}
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea rows={2} value={manualForm.notes}
                          onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })} />
              </div>
              <p className="text-xs text-slate-500">Admin will verify the proof and credit your wallet (usually within a few business hours).</p>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRechargeDialog(false)}>
              <XCircle className="h-4 w-4 mr-1" /> Cancel
            </Button>
            <Button onClick={submitRecharge} disabled={submitting}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="pp-recharge-submit">
              {submitting ? 'Processing…' : (mode === 'easebuzz' ? 'Proceed to Pay' : 'Submit for Approval')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
